import {apiV2, db, DocumentReference, FirebaseError, Query} from "api/firebase";
import {useEffect, useMemo, useState} from "react";
import {uniqBy} from "lodash";
import {useCollectionData} from "react-firebase-hooks/firestore";
import RawOrder, {ExpandedOrder, OrderStatus} from "ts/api/order";
import {ListResult} from "ts/app/list";
import orderRefs from "api/shows/orders/refs";
import {
  IExhibitorUserConnection,
  ISupplierCompany,
} from "@evennt-tech/api-v2-core";
import {IGetAllExhibitorUserConnectionsQueryReq} from "@evennt-tech/api-v2-external";
import uniq from "lodash/uniq";
import {ShowWithRefs} from "ts/api/show";
import {useFetchRefunds} from "hooks/refund";
import useCurrentShowId from "./useCurrentShowId";

type UseShowOrdersResult = [ListResult<ExpandedOrder>, FirebaseError?];

const DEFAULT_PAYMENT_STATUSES = [
  OrderStatus.paid,
  OrderStatus.processing,
  OrderStatus.reviewProcess,
  OrderStatus.reviewCompleted,
  OrderStatus.awaitingPayment,
  OrderStatus.cancelled,
];

export default (
  standId?: string,
  shouldExpandOrder = true,
  orderStatuses = DEFAULT_PAYMENT_STATUSES
): UseShowOrdersResult => {
  const showId = useCurrentShowId();
  const [orders, setOrders] = useState<ExpandedOrder[]>([]);
  const [isReady, setIsReady] = useState(true);

  const showQuery = useMemo(
    () => db.collection("shows").doc(showId) as DocumentReference<ShowWithRefs>,
    [showId]
  );
  const ordersQuery = useMemo(() => {
    let baseOrdersQuery: Query<RawOrder> = orderRefs.getCollection({
      showId: showQuery.id,
    });
    if (orderStatuses?.length) {
      baseOrdersQuery = baseOrdersQuery.where(
        "paymentDetails.status",
        "in",
        orderStatuses
      );
    }
    if (standId) {
      baseOrdersQuery = baseOrdersQuery.where("standId", "==", standId);
    }
    return baseOrdersQuery;
  }, [orderStatuses, standId, showQuery]);
  const [
    showOrders,
    showOrdersLoading,
    showOrdersserror,
  ] = useCollectionData<RawOrder>(ordersQuery);

  useFetchRefunds({orders: showOrders, isReady: !showOrdersLoading});

  useEffect(() => {
    const expandOrderForOrganiser = async (
      order: RawOrder,
      allExhibitorConnections: IExhibitorUserConnection[],
      supplierCompanies: ISupplierCompany[]
    ): Promise<ExpandedOrder | null> => {
      const orderSuppliers: ISupplierCompany[] = order.products
        .map((product) => {
          return supplierCompanies.find(
            (supplierCompany) => supplierCompany.id === product.owner.id
          );
        })
        .filter(Boolean) as ISupplierCompany[];

      const {exhibitorId, standId, exhibitorPairId: orderExhibitorPair} = order;

      const exhibitorConnection = allExhibitorConnections.find(
        ({exhibitorUserId, stand, exhibitorPairId}) => {
          if (orderExhibitorPair) {
            return (
              orderExhibitorPair === exhibitorPairId &&
              exhibitorId === exhibitorUserId
            );
          }
          return exhibitorId === exhibitorUserId && standId === stand?.standId;
        }
      );

      if (!exhibitorConnection) return null;

      return {
        ...order,
        supplierCompanies: uniqBy(orderSuppliers, (supplier) => supplier.name),
        exhibitorConnection,
      } as ExpandedOrder;
    };

    const getOrders = async () => {
      if (!showOrders) {
        return;
      }
      if (!shouldExpandOrder) {
        setOrders(showOrders);
        return;
      }
      setIsReady(false);

      const request: IGetAllExhibitorUserConnectionsQueryReq = {
        exhibitorUserIds: [],
        oldFirebaseStandIds: [],
        showId,
        withDeleted: true,
      };

      for (const order of showOrders) {
        const {exhibitorId, standId} = order;

        if (!request.exhibitorUserIds?.includes(exhibitorId)) {
          request.exhibitorUserIds?.push(exhibitorId);
        }
        if (!request.oldFirebaseStandIds?.includes(standId)) {
          request.oldFirebaseStandIds?.push(standId);
        }
      }

      const allExhibitorConnections =
        request.exhibitorUserIds?.length || request.oldFirebaseStandIds?.length
          ? await apiV2.exhibitor.userConnection.getAll(request)
          : [];

      const supplierCompanyIds = uniq(
        showOrders.flatMap((order) =>
          order.products.map((product) => product.owner.id)
        )
      );

      const supplierCompanies = supplierCompanyIds.length
        ? await apiV2.supplierCompany.getAll({
            supplierCompanyIds,
          })
        : [];

      const expandedOrders = (
        await Promise.all(
          showOrders.map(async (order) => {
            return await expandOrderForOrganiser(
              order,
              allExhibitorConnections,
              supplierCompanies
            );
          })
        )
      ).filter(
        (expandedOrder) => expandedOrder?.exhibitorConnection
      ) as ExpandedOrder[];

      setOrders(expandedOrders);
      setIsReady(true);
    };

    getOrders();
  }, [showOrders, showQuery, shouldExpandOrder]);
  const ordersCount = useMemo(() => {
    if (!orders?.length) {
      return 0;
    }
    return orders.filter((order) =>
      DEFAULT_PAYMENT_STATUSES.includes(order.paymentDetails.status)
    ).length;
  }, [orders]);
  return [
    {
      data: orders,
      total: ordersCount,
      isLoading: showOrdersLoading || !isReady,
    },
    showOrdersserror,
  ];
};
